import { useState, useEffect, forwardRef, Children } from 'react';
import TranslationText from '@Components/Utilities/TranslationText';
import translator from '@Components/Utilities/Translator';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { trackSearchAction } from '@Components/Utilities/Tracking';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { webListingeListingType, homePageCategoryGroup, eventCategory, homePageCategoryTab, classificationConstants } from '@Components/Utilities/SandhillsConstants';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import Combobox from '@Components/MuiWrappers/Combobox';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { setDrillDownCookie } from '@Components/Utilities/CookieHelper';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { convertUTCtoISODateTime } from '@Components/Utilities/SandhillsUtility';
import { getCoordinatesFromAddress } from '@Components/Utilities/Geolocation';
import PopoverMenu from '@Components/MuiWrappers/PopoverMenu';
import { Box, Typography } from "@mui/material";

const Autocomplete = loadable(() => import('@Components/MuiWrappers/Autocomplete'));

const DrillDownSearch = (props) => {
    const theme = useRecoilValue(atoms.muiThemes);
    const windowSize = useWindowSize();
    const drillDownTypes = {
        Category: 1,
        FitsToCategory: 2,
        ClassificationCategory: 3,
        Manufacturer: 4,
        Model: 5,
        Spec: 6,
        ListingType: 7,
        Industries: 8
    };

    const searchType = {
        equipment: 'Equipment',
        attachment: 'Attachment',
        auctionResult: 'AuctionResult',
        keyword: 'Keyword',
        findRentalCompanies: 'FindRentalCompanies'
    };

    const searchTypeOptions = [
        {
            key: searchType.equipment,
            text: props.EquipmentText,
            value: searchType.equipment,
            className: 'drill-search-option'
        },
        {
            key: searchType.attachment,
            text: props.AttachmentText,
            value: searchType.attachment,
            className: 'drill-search-option'
        }
    ];

    const qsParams = {
        EventCategoryID: 'EventCategoryID',
        ClassificationCategory: 'ClassificationCategory',
        ScopeCategoryIDs: 'ScopeCategoryIDs',
        Category: 'Category',
        FTCategory: 'FTCategory',
        Manufacturer: 'Manufacturer',
        Model: 'Model',
        Sort: 'Sort',
        Animal: 'Animal',
        Crop: 'Crop',
        CropHayStraw: 'CropHayStraw',
        Year: 'Year',
        ModelGroup: 'ModelGroup',
        SoldUTCDateTime: 'SoldUTCDateTime',
        ListingType: 'ListingType'
    };

    const desktopComboboxStyles = {
        fontSize: '14px',
        padding: '7px 20px 7px 7px',
        width: 'calc(100% - 10px)',
        textOverflow: 'ellipsis'
    };

    const cityStateZipStylesDesktop = {
        fontSize: '14px',
        padding: '1px 20px 1px 7px',
        width: 'calc(100% - 10px)',
        textOverflow: 'ellipsis'
    };

    const cityStateZipMobileComboboxStyles = {
        fontSize: '16px',
        padding: '1px 7px 1px 7px',
        width: 'calc(100% - 10px)',
        margin: '0 0 5px 0'
    };

    const mobileComboboxStyles = {
        fontSize: 'clamp(13px, 3.5vw, 16px)',
        padding: '7px',
        width: 'calc(100% - 10px)',
        margin: '0 0 5px 0'
    };

    const comboboxPopperStylesDesktop = {
        minWidth: 'fit-content',
        zIndex: 1001
    };

    const comboboxPopperStylesMobile = {
        width: 'calc(75% - 10px)'
    };

    const desktopListboxProps = {
        sx: {
            fontSize: '0.8125rem',
            '& .MuiAutocomplete-option': {
                borderBottom: 'none',
            },
            maxWidth: '40vw'
        },
    };

    const mobileListboxProps = {
        sx: {
            fontSize: '0.8125rem',
            '& .MuiAutocomplete-option': {
                borderBottom: 'none',
            },
        },
    };

    const defaultSearchData = {
        CategoryClassificationDictionary: props.CategoryClassificationDictionary,
        ClassificationCategories: props.ClassificationCategories,
        LoadingClassificationCategories: false,
        Categories: props.Categories,
        LoadingCategories: false,
        FitsToCategories: props.FitsToCategories,
        LoadingFitsToCategories: false,
        Manufacturers: props.Manufacturers,
        Specs: props.Specs,
        ListingTypesDropdown: props.ListingTypesDropdown,
        LoadingListingTypesDropdown: false,
        LoadingManufacturers: false,
        LoadingSpecs: false,
        Models: props.Models,
        LoadingModels: false,
        SearchType: searchType.equipment,
        Category: props.Categories?.[0]?.Text || '',
        FitsToCategory: props.FitsToCategories?.[0]?.Text || '',
        ClassificationCategory: props.ClassificationCategories?.[0]?.Text || '',
        ListingType: props.ListingTypesDropdown?.[0].Text || '',
        Manufacturer: props.Manufacturers?.[0]?.Text || '',
        Model: props.Models?.[0]?.Text || '',
        Spec: props.Specs?.[0]?.Text || '',
        DisableClassificationCategory: true,
        DisableSpecs: true,
        PopulateDrilldownOnLoad: false,
        DisableModels: true,
        DisableCategories: false,
        DisableManufacturers: false,
        DisableEquipmentAttachment: false,
        SpecFields: props.SpecFields,
        Industries: props.Industries,
        Industry: props.Industries?.[0]?.Text || '',
        ExcludedSpecCategories: props.ExcludedSpecCategories,
        ExcludedClassificationCategories: props.ExcludedClassificationCategories,
        LoadingLocations: false,
        Locations: props.Locations,
        CityStateZip: props.CurrentUserFormattedAddress ? props.CurrentUserFormattedAddress : props.Locations?.[0]?.Text || '',
        YearRange: props.YearRange,
        DateRange: props.DateRange,
        IsModelGroup: props.IsModelGroup
    };

    const [activeSearch, setActiveSearch] = useState(props.OverrideActiveTab ? props.OverrideActiveTab : props.HomePageSplitDrilldownCategoryTabs ? props.HomePageSplitDrilldownCategoryTabs[0].Key : props.IsRY ? searchType.findRentalCompanies : searchType.equipment);
    const [searchData, setSearchData] = useState(defaultSearchData);
    const [onlineAuction, setOnlineAuction] = useState(false);
    const [onSiteAuction, setOnSiteAuction] = useState(false);
    const [comboboxStyles, setComboboxStyles] = useState(desktopComboboxStyles);
    const [comboboxPopperStyles, setComboboxPopperStyles] = useState(comboboxPopperStylesDesktop);
    const [listboxProps, setListboxProps] = useState(desktopListboxProps);
    const [selectedSplitDrilldownCategoryTab, setSelectedSplitDrilldownCategoryTab] = useState(props.HomePageSplitDrilldownCategoryTabs ? (props.OverrideActiveTab ?? props.HomePageSplitDrilldownCategoryTabs[0]) : null);
    const [useClassification, setUseClassifications] = useState(props.UseClassifications);
    const [openedDropdowns, setOpenedDropdowns] = useState([]);
    const [prevValues, setPrevValues] = useState({});
    const [categoryDrilldown, setCategoryDrilldown] = useState(true);
    const [categoryInfo, setCategoryInfo] = useState({
        CategoryName: searchData?.Categories[0]?.CleanName ? searchData?.Categories[0]?.CleanName : searchData?.Categories[0]?.Text,
        CategoryID: searchData?.Categories[0]?.Value,
        ParentCategoryID: searchData?.Categories[0]?.ParentValue,
        ClassificationCategoryID: searchData?.Categories[0]?.ClassificationCategoryID
    });
    const [cityStateZipPlaceholder, setCityStateZipPlaceholder] = useState(translator.translate('sandhills.citystatezip'));
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const currentUser = useSetRecoilState(atoms.currentUser);
    const routes = useRecoilValue(atoms.routes);

    useEffect(() => {
        setSearchData(previousState => {
            return {
                ...previousState,
                Industry: findValidValue([props.IndustryOverride?.Value, props.Industries?.[0]?.Text]),
                Category: findValidValue([props.CatOverride?.Value, props.Categories?.[0]?.Text]),
                FitsToCategory: findValidValue([props.FitsToOverride?.Value, props.FitsToCategories?.[0]?.Text]),
                ClassificationCategory: findValidValue([props.ClassificationOverride?.Value, props.ClassificationCategories?.[0]?.Text]),
                Manufacturer: findValidValue([props.ManuOverride?.Value, props.Manufacturers?.[0]?.Text]),
                Model: findValidValue([props.ModelOverride?.Value, props.Models?.[0]?.Text]),
                Spec: findValidValue([props.SpecOverride?.Value, props.Specs?.[0]?.Text]),
                ListingType: findValidValue([props.ListingTypeOverride?.Value, props.ListingTypesDropdown?.[0].Text]),
                DisableModels: (props.Manufacturers?.[0].Value != 'undefined' && props.Manufacturers?.[0].Value != '') || (props.ManuOverride?.Value != '' && props.ManuOverride?.Value != null) ? false : true,
                SearchType: props.ARTypeOverride || searchType.equipment,
                Industries: (props.ARTypeOverride === searchType.attachment && props.ExcludeFromAttachmentScopeCategories) ? props.Industries.filter(item => !props.ExcludeFromAttachmentScopeCategories.includes(parseInt(item.UrlNormalizedValue))) : props.Industries,
                CityStateZip: props.CurrentUserFormattedAddress ? props.CurrentUserFormattedAddress : props.Locations?.[0]?.Text || '',
                YearRange: props.YearRange,
                DateRange: props.DateRange
            }
        })
    }, [])

    useEffect(() => {
        setSearchData(defaultSearchData);
        if (props.PopulateDrilldownOnLoad && !props.CatOverride) {
            populateSelectList({ drillDownType: drillDownTypes.Category });
        }
        if (props.IndustryOverride?.Value === classificationConstants.haystraw || props.IndustryOverride?.Value === classificationConstants.livestock) {
            setUseClassifications(true);
            if (props.IndustryOverride?.Value === classificationConstants.haystraw) {
                setCategoryDrilldown(false);
            }

        }
        setOnlineAuction(props.CategoryGroupID == homePageCategoryGroup.onlineAuction);
        setOnSiteAuction(props.CategoryGroupID == homePageCategoryGroup.onSiteAuction);
        setSearchData(previousState => {
            return {
                ...previousState,
                Industry: findValidValue([props.IndustryOverride?.Value, props.Industries?.[0]?.Text]),
                Category: findValidValue([props.CatOverride?.Value, props.Categories?.[0]?.Text]),
                FitsToCategory: findValidValue([props.FitsToOverride?.Value, props.FitsToCategories?.[0]?.Text]),
                ClassificationCategory: findValidValue([props.ClassificationOverride?.Value, props.ClassificationCategories?.[0]?.Text]),
                Manufacturer: findValidValue([props.ManuOverride?.Value, props.Manufacturers?.[0]?.Text]),
                Model: findValidValue([props.ModelOverride?.Value, props.Models?.[0]?.Text]),
                Spec: findValidValue([props.SpecOverride?.Value, props.Specs?.[0]?.Text]),
                ListingType: findValidValue([props.ListingTypeOverride?.Value, props.ListingTypesDropdown?.[0].Text]),
                DisableClassificationCategory: (props.CatOverride?.Value != 'undefined' && props.CatOverride?.Value != '' && props.CatOverride?.Value != null) || props.ExcludedClassificationCategories.includes(props.CatOverride?.Value) || props.IndustryOverride?.Value === classificationConstants.haystraw ? false : true,
                DisableSpecs: (findValidValue([props.CatOverride?.Value]) === '' || props.ExcludedSpecCategories?.includes(props.CatOverride?.Value)) && !(props.IndustryOverride?.Value === classificationConstants.haystraw),
                DisableEquipmentAttachment: (activeSearch === searchType.auctionResult && props.ExcludeFromAttachmentScopeCategories && props.ExcludeFromAttachmentScopeCategories.includes(parseInt(props.IndustryOverride?.Value))) ? true : false,
                DisableCategories: defaultSearchData.DisableCategories,
                DisableManufacturers: (props.IndustryOverride?.Value === classificationConstants.land || props.LivestockAndHaystrawCategories?.includes(props.CatOverride?.Value)) ? true : false,
                DisableYear: props.LivestockAndHaystrawCategories?.includes(props.CatOverride?.Value),
                DisableModels: (props.Manufacturers?.[0].Value != 'undefined' && props.Manufacturers?.[0].Value != '') || (props.ManuOverride?.Value != '' && props.ManuOverride?.Value != null) ? false : true,
                SearchType: props.ARTypeOverride || searchType.equipment,
                Industries: (props.ARTypeOverride === searchType.attachment && props.ExcludeFromAttachmentScopeCategories) ? props.Industries.filter(item => !props.ExcludeFromAttachmentScopeCategories.includes(parseInt(item.UrlNormalizedValue))) : props.Industries,
                CityStateZip: props.CurrentUserFormattedAddress ? props.CurrentUserFormattedAddress : props.Locations?.[0]?.Text || '',
                YearRange: props.YearRange,
                DateRange: props.DateRange
            }
        })
    }, [props.CategoryGroupID]);

    useEffect(() => {
        if (searchData.PopulateDrilldownOnLoad && searchData.Categories && searchData.Categories.length > 0) {
            setCategoryInfo({ CategoryID: searchData.Categories[0].Value, CategoryName: searchData.Categories[0].CleanName, ClassificationCategoryID: searchData.Categories[0].CategoryClassificationID });
        }
    }, [searchData.PopulateDrilldownOnLoad]);

    useEffect(() => {
        if (windowSize.isMobile) {
            setComboboxStyles(mobileComboboxStyles);
            setComboboxPopperStyles(comboboxPopperStylesMobile);
            setListboxProps(mobileListboxProps);
        }
        else {
            setComboboxStyles(desktopComboboxStyles);
            setComboboxPopperStyles(comboboxPopperStylesDesktop);
            setListboxProps(desktopListboxProps);
        }
    }, [windowSize.isMobile]);

    const findValidValue = (values) => {
        const validValue = values.find(value => value != null && value != 'undefined' && value != '');
        return validValue !== undefined ? validValue : '';
    };

    const populateLocationAutoSuggestions = ({
        CityStateZip = selected.CityStateZip
    } = {}) => {
        setCityStateZipPlaceholder('');
        let data = {
            inputText: CityStateZip
        };
        return sandhillsRequest({
            url: routes.LocationAutosuggestUrl,
            data: data,
            errorHandling: {
                message: 'Error retrieving locations.',
                showDetails: true,
                useConsole: true,
                useAlert: false
            }
        }).then((response) => {
            setSearchData(previousState => {
                return {
                    ...previousState,
                    Locations: response,
                    CityStateZip: CityStateZip
                };
            });
        });
    }

    const populateSelectList = ({
        drillDownType,
        category = selected.Category,
        manufacturer = selected.Manufacturer,
        listingType = selected.ListingType,
        spec = selected.Spec,
        classificationCategory = selected.ClassificationCategory,
        forcePopulate = false,
        industry = selectedSplitDrilldownCategoryTab?.CategoryIDs || searchData.Industry,
        model = selected.Model,
        isModelGroup = false,
        isDismantledDrilldown = false
    } = {}) => {
        if (drillDownType) {
            let handleResponse = null;
            let data = {
                eListingType: getListingType(listingType),
                eDrillDownField: drillDownType,
                isAttachmentSearch: !isDismantledDrilldown && (activeSearch === searchType.attachment || searchData.SearchType === searchType.attachment)
            };
            if (industry && industry != 'Market' && !industry?.isDefault) {
                if (searchData.SearchType === searchType.attachment) {
                    data.scopedCatID = props.EquipmentToAttachmentScopeCategories[industry];
                } else {
                    data.scopedCatID = industry;
                }
            }
            if (industry === classificationConstants.haystraw) {
                category = classificationConstants.haystrawCategory;
            }
            if (onlineAuction) {
                data.EventCategoryID = eventCategory.onlineAuction;
            } else if (onSiteAuction) {
                data.EventCategoryID = eventCategory.onSiteAuction;
            }

            if (drillDownType == drillDownTypes.ListingType && !searchData.LoadingListingTypesDropdown && (forcePopulate || !searchData.ListingTypesDropdown || searchData.ListingTypesDropdown.length <= 1)) {
                setSearchData(previousState => { return { ...previousState, LoadingListingTypesDropdown: true }; });
                handleResponse = (response) => {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            ListingTypesDropdown: response.ListingTypesDropdown,
                            Categories: response.Categories,
                            CategoryClassificationDictionary: response.CategoryClassificationDictionary,
                            LoadingListingTypesDropdown: false,
                            PopulateDrilldownOnLoad: props.PopulateDrilldownOnLoad
                        };
                    });
                };
            } else if (drillDownType == drillDownTypes.Manufacturer && !searchData.LoadingManufacturers && (forcePopulate || !searchData.Manufacturers || searchData.Manufacturers.length <= 2)) {
                setSearchData(previousState => { return { ...previousState, LoadingManufacturers: true }; });
                if (props.ShowDrilldownSearchOnBottom && category) {
                    data.Category = getSelectedCategories([category], category, searchData.Categories).join('|');
                }
                handleResponse = (response) => {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            Manufacturers: response?.Manufacturers,
                            LoadingManufacturers: false
                        };
                    });
                };
            } else if (drillDownType == drillDownTypes.Model && !searchData.LoadingModels && (forcePopulate || !searchData.Models || searchData.Models.length <= 2)) {
                setSearchData(previousState => { return { ...previousState, LoadingModels: true }; });
                data.Manufacturer = manufacturer;
                if (props.ShowDrilldownSearchOnBottom && category) {
                    data.Category = getSelectedCategories([category], category, searchData.Categories).join('|');
                }
                handleResponse = (response) => {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            Models: response?.Models,
                            LoadingModels: false
                        };
                    });
                };
            } else if (drillDownType == drillDownTypes.Category && !searchData.LoadingCategories && (forcePopulate || !searchData.Categories || searchData.Categories.length <= 2)) {
                setSearchData(previousState => { return { ...previousState, LoadingCategories: true }; });
                if (!props.ShowDrilldownSearchOnBottom) {
                    if (manufacturer && manufacturer !== defaultSearchData.Manufacturer) {
                        data.Manufacturer = manufacturer;
                    }

                    if (model && model !== defaultSearchData.Model) {
                        if (isModelGroup) {
                            data.ModelGroup = model;
                        }
                        else {
                            data.Model = model;
                        }
                    }

                    handleResponse = (response) => {
                        setSearchData(previousState => {
                            return {
                                ...previousState,
                                Categories: response?.Categories,
                                LoadingCategories: false
                            };
                        });
                    };
                }
                else {
                    if (spec && spec !== defaultSearchData.Spec) {
                        data.Spec = spec;
                    }

                    if (classificationCategory && classificationCategory !== defaultSearchData.ClassificationCategory) {
                        data.ClassificationCategory = classificationCategory;
                    }

                    handleResponse = (response) => {
                        setSearchData(previousState => {
                            return {
                                ...previousState,
                                Category: forcePopulate && props.CatOverride?.Value ? props.CatOverride.Value : previousState.Category,
                                Categories: response.Categories,
                                CategoryClassificationDictionary: industry ? props.CategoryClassificationDictionary : response.CategoryClassificationDictionary,
                                LoadingCategories: false,
                                PopulateDrilldownOnLoad: props.PopulateDrilldownOnLoad
                            };
                        });
                    };
                }
            } else if (drillDownType == drillDownTypes.FitsToCategory && !searchData.LoadingFitsToCategories && (forcePopulate || !searchData.FitsToCategories || searchData.FitsToCategories.length <= 2)) {
                setSearchData(previousState => { return { ...previousState, LoadingFitsToCategories: true }; });
                if (category) {
                    data.Category = getSelectedCategories([category], category, searchData.Categories).join('|');
                }
                handleResponse = (response) => {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            FitsToCategories: response?.FitsToCategories,
                            LoadingFitsToCategories: false
                        };
                    });
                };
            } else if (drillDownType == drillDownTypes.ClassificationCategory && !searchData.LoadingClassificationCategories && (forcePopulate || !searchData.ClassificationCategories || searchData.ClassificationCategories.length <= 2)) {
                setSearchData(previousState => { return { ...previousState, LoadingClassificationCategories: true }; });
                let val = '';
                if (category && searchData.CategoryClassificationDictionary) {
                    let specFieldName = defaultSearchData.SpecFields[category];
                    if (spec && specFieldName) {
                        data[specFieldName] = spec;
                    }
                    let classification = searchData.CategoryClassificationDictionary[parseInt(category) || parseInt(category.value)];
                    if (classification) {
                        data[classification] = category;
                        val = classification;
                    }
                }
                handleResponse = (response) => {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            ClassificationCategories: response?.ClassificationCategories,
                            ClassificationCategory: data[val] == props.CatOverride?.Value && props.ClassificationOverride?.Value != null && response.ClassificationCategories.filter((c) => c.value == props.ClassificationOverride?.Value).length > 0 ? props.ClassificationOverride?.Value : response?.ClassificationCategories?.[0]?.Text || '',
                            LoadingClassificationCategories: false
                        };
                    });
                };
            } else if (drillDownType == drillDownTypes.Spec && !searchData.LoadingSpecs && (forcePopulate || !searchData.Specs || searchData.Specs?.length <= 2)) {
                setSearchData(previousState => { return { ...previousState, LoadingSpecs: true }; });
                let value = '';
                if (category) {
                    if (searchData.CategoryClassificationDictionary) {
                        let classification = searchData.CategoryClassificationDictionary[parseInt(category)];
                        if (classification) {
                            data[classification] = category;
                            value = classification;
                        }
                    }
                    else {
                        data.Category = getSelectedCategories([category], category, searchData.Categories).join('|');
                    }
                }

                handleResponse = (response) => {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            Specs: response?.Specs,
                            Spec: data[value] == props.CatOverride?.Value && (data[value] && props.SpecOverride?.Value) ? props.SpecOverride?.Value : searchData.Specs?.[0]?.Text || '',
                            LoadingSpecs: false
                        };
                    });
                };
            }

            if (handleResponse) {
                return sandhillsRequest({
                    url: routes.GetDrilldownSearch,
                    data: data,
                    errorHandling: {
                        message: 'Error retrieving drilldown options.',
                        showDetails: true,
                        useConsole: true,
                        useAlert: false
                    }
                }).then((response) => {
                    handleResponse(response);
                });
            }
            //Solves spinner issue when dropdown is opened while data is loading
            return Promise.resolve();
        }
    };

    const generateKey = (item, options) => {
        let key = item.Text;
        key = generateChildKeys(key, item, options);
        return !findValidValue([item.SortID]) ? key : key + ' ' + item.SortID;
    };

    const generateChildKeys = (key, item, options) => {
        if (item && item.IsParent) {
            options.forEach(child => {
                if (child.ParentValue === item.Value) {
                    key += ' ' + child.Text;
                    generateChildKeys(key, child, options);
                }
            });
        }
        return key;
    };

    const getSelectedCategories = (categoryIds, currentId, categories, isFitsTo) => {
        let selectedCategory = categories.find(cat => cat.Value === currentId || currentId.value);
        if (selectedCategory && selectedCategory.IsParent) {
            if (isFitsTo) {
                categoryIds = categoryIds.filter(id => id !== selectedCategory.Value);
            }

            categories.forEach((cat) => {
                if (cat.ParentValue === selectedCategory.Value) {
                    if (!isFitsTo || !cat.IsParent) {
                        categoryIds.push(cat.Value);
                    }

                    categoryIds = getSelectedCategories(categoryIds, cat.Value, categories, isFitsTo);
                }
            });
        }

        return categoryIds;
    };

    const getSelectedModels = (selectedModel) => {
        let models = [];
        if (selectedModel.IsParent) {
            searchData.Models.forEach(model => {
                if (model.ParentValue === selectedModel.Value) {
                    models.push(model.Value);
                }
            });
        } else {
            models.push(selectedModel.Value);
        }

        return models;
    };

    const getListingType = (listingOverride) => {
        if (props.DismantledSearch) {
            return webListingeListingType.dismantled;
        } else if (onlineAuction || onSiteAuction) {
            return webListingeListingType.auction;
        } else if (activeSearch === searchType.auctionResult || props.IsAuctionResultsOnly) {
            return webListingeListingType.auctionResult;
        } else if (listingOverride == translator.translate('sandhills.forrent')) {
            return webListingeListingType.forRent;
        } else if (props.IsUpcomingAuctionsOnly) {
            return webListingeListingType.auction;
        } else {
            return webListingeListingType.forSale;
        }
    };

    const getDrillButtonsCSSClass = () => {
        let btnCount = 2;
        if (props.AttachmentScopeCategories && props.AttachmentScopeCategories.length > 0) {
            btnCount++;
        }
        if (props.ShowAuctionResults) {
            btnCount++;
        }

        return 'drill-buttons drill-buttons-' + btnCount;
    };

    const getValueIfNotDefault = (val, defaultVal) => val != defaultVal && val != "undefined" ? val : '';

    const addQueryStringParameter = (queryString, name, value) => {
        return queryString.push(`${name}=${encodeURIComponent(value)}`);
    };

    const selected = {
        Category: getValueIfNotDefault(searchData.Category, defaultSearchData.Category),
        ClassificationCategory: getValueIfNotDefault(searchData.ClassificationCategory, searchData.ClassificationCategories?.[0]?.Text || ''),
        Manufacturer: getValueIfNotDefault(searchData.Manufacturer, defaultSearchData.Manufacturer),
        Model: getValueIfNotDefault(searchData.Model, defaultSearchData.Model),
        Spec: getValueIfNotDefault(searchData.Spec, defaultSearchData.Spec),
        FTCategory: getValueIfNotDefault(searchData.FitsToCategory, defaultSearchData.FitsToCategory),
        ListingType: getValueIfNotDefault(searchData.ListingType, defaultSearchData.ListingType),
        CityStateZip: getValueIfNotDefault(searchData.CityStateZip, defaultSearchData.CityStateZip) || defaultSearchData.CityStateZip,
        Industry: getValueIfNotDefault(searchData.Industry, defaultSearchData.Industry),
        YearRange: getValueIfNotDefault(searchData.YearRange, defaultSearchData.YearRange),
        DateRange: getValueIfNotDefault(searchData.DateRange, defaultSearchData.DateRange)
    };

    const getDefaultSearchURL = (key) => {
        switch (key) {
            case 'Equipment':
                return props.SearchURLs.DefaultEquipmentListPageUrl;
            case 'Dismantled':
                return props.SearchURLs.DefaultDismantledListPageUrl;
            case 'Attachment':
                return props.SearchURLs.DefaultAttachmentsListPageUrl;
            case 'AuctionResult':
                if (searchData.SearchType === searchType.attachment) {
                    return props.SearchURLs.DefaultAttachmentAuctionResultListPageUrl;
                }
                else {
                    return props.SearchURLs.DefaultEquipmentAuctionResultListPageUrl;
                }
            default:
                return props.SearchURLs.DefaultEquipmentListPageUrl;
        }
    };

    const drilldownSearch = (e, isDismantledDrilldown = false) => {
        e.preventDefault();

        if (selectedSplitDrilldownCategoryTab && !selected.Category && !selected.Manufacturer && !selected.Model && props.AllListingsURL) {
            window.location.href = props.AllListingsURL;
            setOpenedDropdowns([]);
            return;
        }
        let queryString = [];
        let categoryIds = [];
        let manufacturers = [];
        let models = [];

        let selectedCat = (selected.Industry === classificationConstants.haystraw) ? classificationConstants.haystrawCategory : selected.Category;
        let selectedClassCat = selected.ClassificationCategory || (selectedCat === classificationConstants.haystrawCategory ? "Class" : "Bale Type");
        let selectedManu = selected.Manufacturer;
        let selectedModel = selected.Model;
        let selectedSpec = selected.Spec;
        let selectedFTCat = selected.FTCategory;
        let selectedListingType = [
            webListingeListingType.dismantled,
            webListingeListingType.forSale,
            webListingeListingType.forRent,
        ].includes(getListingType(selected.ListingType));
        let selectedCityStateZip = selected.CityStateZip;
        let selectedIndustry = selected.Industry;
        let yearRange = selected.YearRange;
        let dateRange = selected.DateRange;
        let isModelGroup = searchData.IsModelGroup;
        let otherItems = (searchData?.Industry === classificationConstants.otherstock);

        // If no drilldown items are selected, go to the default search url for the active tab
        const hasDrilldownSelection = Boolean(
            selectedListingType ||
            selectedCat ||
            selectedManu ||
            selectedModel ||
            selectedSpec ||
            selectedFTCat ||
            (selectedIndustry && selectedIndustry !== 'Market') 
        );

        if (!hasDrilldownSelection) {
            // persist cookie with current (possibly empty) selections
            setDrillDownCookie(selected.Industry, selected.Category, isDismantledDrilldown ? "" : selected.Manufacturer || selected.ClassificationCategory, isDismantledDrilldown ? "" : selected.Model || selected.Spec, isDismantledDrilldown ? selected.Manufacturer : "", isDismantledDrilldown ? selected.Model : "", activeSearch, selected.FTCategory, searchData.SearchType, selected.ListingType, searchData.IsModelGroup);

            if (isDismantledDrilldown) {
                window.location.href = getDefaultSearchURL("Dismantled");
            } else if (activeSearch === searchType.findRentalCompanies) {
                window.location.href = props.DealerDirectoryUrl;
            } else if (!props.ShowDrilldownSearchOnBottom && !currentSite?.Site?.IsMDTrader) {
                window.location.href = props.Url + '/search';
            } else {
                window.location.href = getDefaultSearchURL(activeSearch);
            }

            setOpenedDropdowns([]);
            return;
        }

        if (onlineAuction) {
            addQueryStringParameter(queryString, qsParams.EventCategoryID, eventCategory.onlineAuction);
        }
        else if (onSiteAuction) {
            addQueryStringParameter(queryString, qsParams.EventCategoryID, eventCategory.onSiteAuction);
        }

        if (!isDismantledDrilldown && activeSearch === searchType.auctionResult) {
            addQueryStringParameter(queryString, qsParams.ListingType, "Auction Results");
        }

        if (!isDismantledDrilldown && (activeSearch === searchType.attachment || searchData.SearchType === searchType.attachment) && props.AttachmentScopeCategories?.length > 0) {
            addQueryStringParameter(queryString, qsParams.ScopeCategoryIDs, props.AttachmentScopeCategories.join('|'));
        }
        else if (otherItems) {
            addQueryStringParameter(queryString, qsParams.ScopeCategoryIDs, searchData.Industry);
        }

        let category = null;
        if (selectedCat) {
            category = searchData.Categories.find(cat => cat.Value === selectedCat);
            categoryIds = getSelectedCategories([selectedCat], selectedCat, searchData.Categories);

            let classificationCategoryID = category.CategoryClassificationID;
            if (classificationCategoryID && category?.Value) {
                const isLivestock = classificationCategoryID >= 1 && classificationCategoryID <= 3;
                const hasParentValue = classificationCategoryID > 1;
                const scopeCategoryID = isLivestock ? classificationConstants.livestock : classificationConstants.haystraw;

                if (!isLivestock) {
                    if (selectedClassCat || selectedSpec) {
                        (category.Value === classificationConstants.haystrawCategory) && addQueryStringParameter(queryString, qsParams.Crop, category.Value);

                        let classificationCategory = searchData.ClassificationCategories?.find(cat => cat.Value === selectedClassCat);
                        (classificationCategory?.Value) && addQueryStringParameter(queryString, qsParams.ClassificationCategory, classificationCategory.Value);

                        let specFieldName = searchData.SpecFields?.[category.Value] ?? '';
                        let specField = searchData.Specs?.find(spec => spec.Value === selectedSpec);
                        (specFieldName && specField?.Value) && addQueryStringParameter(queryString, specFieldName, specField.Value);
                    }
                    else {
                        if (category.ParentValue && category.ParentValue != classificationConstants.haystraw) {
                            addQueryStringParameter(queryString, qsParams.Crop, category.ParentValue);
                            addQueryStringParameter(queryString, qsParams.ClassificationCategory, category.Value);
                        }
                        else {
                            addQueryStringParameter(queryString, qsParams.Crop, category.Value);
                        }
                    }
                }
                else if (isLivestock) {
                    switch (classificationCategoryID) {
                        case 1:
                            if (selectedClassCat || selectedSpec) {
                                addQueryStringParameter(queryString, qsParams.Animal, category.Value);

                                let classificationCategory = searchData.ClassificationCategories?.find(cat => cat.Value === selectedClassCat);
                                (classificationCategory?.Value) && addQueryStringParameter(queryString, qsParams.ClassificationCategory, classificationCategory.Value);

                                let specFieldName = searchData.SpecFields[category.Value];
                                let specField = searchData.Specs.find(spec => spec.Value === selectedSpec);
                                (specFieldName && specField?.Value) && addQueryStringParameter(queryString, specFieldName, specField.Value);
                            }
                            else {
                                if (category.ParentValue && category.ParentValue != classificationConstants.livestock) {
                                    addQueryStringParameter(queryString, qsParams.Animal, category.ParentValue);
                                    addQueryStringParameter(queryString, qsParams.ClassificationCategory, category.Value);
                                }
                                else {
                                    addQueryStringParameter(queryString, qsParams.Animal, category.Value);
                                }
                            }
                            break;
                        case 2:
                            addQueryStringParameter(queryString, qsParams.ClassificationCategory, category.Value);
                            (category.ParentValue) && addQueryStringParameter(queryString, qsParams.Animal, category.ParentValue);
                            break;
                        case 3:
                            addQueryStringParameter(queryString, qsParams.ClassificationCategory, category.Value);

                            if (searchData.Categories.length <= 2) {
                                (category.ParentValue) && addQueryStringParameter(queryString, qsParams.Animal, category.ParentValue);
                            }
                            else {
                                let parentCategory = searchData.Categories.find(cat => cat.Value === category?.ParentValue);
                                let animalCat = searchData.Categories.find(cat => cat.Value === parentCategory?.ParentValue);
                                (category.ParentValue && !animalCat?.Value) && addQueryStringParameter(queryString, qsParams.Animal, category.ParentValue);
                                (animalCat?.Value) && addQueryStringParameter(queryString, qsParams.Animal, animalCat.Value);
                            }
                    }
                }
            }
            else {
                if (category.Value === classificationConstants.livestock) {
                    addQueryStringParameter(queryString, qsParams.ScopeCategoryIDs, category.Value);
                }

                else if (category.Value === classificationConstants.haystraw) {
                    addQueryStringParameter(queryString, qsParams.Crop, Object.entries(searchData.CategoryClassificationDictionary).find(([key, val]) => val === qsParams.Crop)[0]);
                }
                else {
                    addQueryStringParameter(queryString, qsParams.Category, category.Value);
                }
            }
        }

        if (selectedFTCat) {
            let ftCategory = searchData.FitsToCategories.find(cat => cat.Value === selectedFTCat);
            categoryIds.push(ftCategory);
            ftCategory && addQueryStringParameter(queryString, qsParams.FTCategory, ftCategory.Value);
        }

        if (selectedManu) {
            let manufacturer = searchData.Manufacturers.find(manu => manu.Value === selectedManu);
            manufacturers.push(manufacturer)
            manufacturer && addQueryStringParameter(queryString, qsParams.Manufacturer, manufacturer.Value);
        }

        if (selectedModel) {
            models = getSelectedModels(selectedModel);
            addQueryStringParameter(queryString, (isModelGroup || searchData.Model.IsParent) ? qsParams.ModelGroup : qsParams.Model, selectedModel);
        }

        if (activeSearch === searchType.findRentalCompanies && selectedCityStateZip) {
            addQueryStringParameter(queryString, qsParams.Sort, '6');
        }

        if (yearRange && (yearRange.minimum || yearRange.maximum)) {
            addQueryStringParameter(queryString, qsParams.Year, yearRange.minimum + '*' + yearRange.maximum);
        }

        if (dateRange) {
            addQueryStringParameter(queryString, qsParams.SoldUTCDateTime, convertUTCtoISODateTime(dateRange.minDate) + '*' + convertUTCtoISODateTime(dateRange.maxDate));
        }

        if (selectedIndustry && !selectedCat && !selectedManu && !otherItems) { //Industry only selected while category and manufacturer is set to all
            if (selectedIndustry === classificationConstants.livestock) {
                addQueryStringParameter(queryString, qsParams.ScopeCategoryIDs, selectedIndustry);
            } else if (selectedIndustry != "Market") {
                addQueryStringParameter(queryString, qsParams.Category, selectedIndustry);
            }

        }

        setDrillDownCookie(selected.Industry, selected.Category, isDismantledDrilldown ? "" : selected.Manufacturer || selected.ClassificationCategory, isDismantledDrilldown ? "" : selected.Model || selected.Spec, isDismantledDrilldown ? selected.Manufacturer : "", isDismantledDrilldown ? selected.Model : "", activeSearch, selected.FTCategory, searchData.SearchType, selected.ListingType, searchData.IsModelGroup);
        if (activeSearch === searchType.findRentalCompanies) {
            window.location.href = props.DealerDirectoryUrl + (queryString.length > 0 ? ('?' + queryString.join('&')) : '');
        } else if (!props.ShowDrilldownSearchOnBottom && !currentSite?.Site?.IsMDTrader) {
            window.location.href = props.Url + '/search' + '?' + queryString.join('&');
        } else {
            if (props.IsRY) {
                selected.ListingType = translator.translate('sandhills.forrent');
            }
            let listingTypeIndex = getListingType(selected.ListingType);
            let listingsTypeForSearch = props.ListingTypes[listingTypeIndex];

            if (routes.SaveAction) {
                let listingsTypeForTracking = listingTypeIndex === webListingeListingType.forSale ? '' : props.ListingTypes[listingTypeIndex];
                let isDefaultListingTypeSearch = listingTypeIndex === webListingeListingType.forSale;
                let conditions = listingTypeIndex === webListingeListingType.dismantled ? 'dismantled' : [];
                trackSearchAction(routes.SaveAction, '', categoryIds, manufacturers, models, [listingsTypeForTracking], isDefaultListingTypeSearch, searchData.SearchType === searchType.attachment, conditions);
            }

            window.location.href = props.Url + '/' + listingsTypeForSearch + (queryString.length > 0 ? ('?' + queryString.join('&')) : '');
        }
        setOpenedDropdowns([]);
        return;
    };

    const drilldownChange = ({
        category = searchData.Category,
        fitsToCategory = searchData.FitsToCategory,
        classificationCategory = searchData.ClassificationCategory,
        listingType = searchData.ListingType,
        manufacturer = searchData.Manufacturer,
        model = searchData.Model,
        spec = searchData.Spec,
        Industry = searchData.Industry,
        SearchType = searchData.SearchType,
        isModelGroup = searchData.IsModelGroup,
        isDismantledDrilldown = false
    } = {}) => {
        defaultSearchData.Category = props.Categories?.[0]?.Text || '';
        defaultSearchData.Manufacturer = props.Manufacturers?.[0]?.Text || '';

        let categoryHasChanged = category != searchData.Category;
        let manuHasChanged = manufacturer != searchData.Manufacturer;
        let modelHasChanged = model != searchData.Model;
        let specHasChanged = spec != searchData.Spec;
        let industryHasChanged = Industry != searchData.Industry;
        let searchTypeHasChanged = SearchType != searchData.SearchType;
        let listingTypeHasChanged = listingType != searchData.ListingType;

        let currentCategory = category === '' || listingTypeHasChanged || industryHasChanged || ((manuHasChanged || modelHasChanged) && !props.ShowDrilldownSearchOnBottom) ? defaultSearchData.Category : category && category.text ? defaultSearchData.Category : category;
        let currentCategories = (listingTypeHasChanged || industryHasChanged || ((modelHasChanged || manuHasChanged) && !props.ShowDrilldownSearchOnBottom)) ? props.Categories : searchData.Categories;
        let currentManufacturer = (manufacturer === '' || (categoryHasChanged && props.ShowDrilldownSearchOnBottom) || listingTypeHasChanged || industryHasChanged) ? defaultSearchData.Manufacturer : manufacturer;
        let currentManufacturers = ((categoryHasChanged && props.ShowDrilldownSearchOnBottom) || listingTypeHasChanged || industryHasChanged) ? props.Manufacturers : searchData.Manufacturers;
        let currentModel = (model === '' || (categoryHasChanged && props.ShowDrilldownSearchOnBottom) || manuHasChanged || listingTypeHasChanged || industryHasChanged) ? defaultSearchData.Model : model;
        let currentModels = ((categoryHasChanged && props.ShowDrilldownSearchOnBottom) || manuHasChanged || listingTypeHasChanged || industryHasChanged) ? props.Models : searchData.Models;
        let currentSpec = (spec === '' || categoryHasChanged || industryHasChanged) ? defaultSearchData.Spec : spec;
        let currentSpecs = categoryHasChanged ? props.Specs : searchData.Specs;
        let currentClassificationCategory = (classificationCategory === '' || categoryHasChanged || specHasChanged || industryHasChanged) ? searchData.ClassificationCategories?.[0]?.Text || '' : classificationCategory;
        let currentClassificationCategories = (categoryHasChanged || specHasChanged || industryHasChanged) ? props.ClassificationCategories : searchData.ClassificationCategories;
        let currentFitsToCategory = (categoryHasChanged || listingTypeHasChanged) ? defaultSearchData.FitsToCategory : fitsToCategory;
        let currentFitsToCategories = (categoryHasChanged || listingTypeHasChanged) ? props.FitsToCategories : searchData.FitsToCategories;
        let currentIndustry = industryHasChanged ? Industry : searchData.Industry;

        let disableManufacturers = Industry === classificationConstants.land || props.LivestockAndHaystrawCategories?.includes(currentCategory);
        let disableModels = !getValueIfNotDefault(currentManufacturer, defaultSearchData.Manufacturer);
        let disableSpecs = props.ExcludedSpecCategories?.includes(currentCategory);
        let disableClassificationCategory = (getValueIfNotDefault(category, defaultSearchData.Category) || (Industry === classificationConstants.haystraw)) && !props.ExcludedClassificationCategories?.includes(currentCategory) ? false : true;
        let disableEquipmentAttachment = activeSearch === searchType.auctionResult && props.ExcludeFromAttachmentScopeCategories && props.ExcludeFromAttachmentScopeCategories.includes(parseInt(Industry));
        let disableYear = props.LivestockAndHaystrawCategories?.includes(currentCategory);

        if (currentSite?.Site?.IsBidcaller || currentSite?.Site?.IsAuctionTime) {
            if (Industry === classificationConstants.livestock) {
                if (category === classificationConstants.haystrawCategory) {
                    category = defaultSearchData.Category;
                    searchData.Category = defaultSearchData.Category;
                }
                setUseClassifications(true);
                setCategoryDrilldown(true);
            }
            else if (Industry === classificationConstants.haystraw) {
                category = classificationConstants.haystrawCategory;
                searchData.Category = classificationConstants.haystrawCategory;
                props.CatOverride.Value = null;
                defaultSearchData.Category = classificationConstants.haystrawCategory;
                setUseClassifications(true);
                setCategoryDrilldown(false);
            }
            else {
                if (industryHasChanged) {
                    category = defaultSearchData.Category;
                    searchData.Category = defaultSearchData.Category;
                }
                setUseClassifications(false);
                setCategoryDrilldown(true);
            }
        }
        if (Industry?.text === props.Industries?.[0]?.Text) { Industry = props.Industries?.[0]?.Text; }

        setSearchData(previousState => {
            return {
                ...previousState,
                Category: currentCategory, Categories: currentCategories, DisableCategories: defaultSearchData.DisableCategories,
                Manufacturer: currentManufacturer, Manufacturers: currentManufacturers, DisableManufacturers: disableManufacturers,
                Model: currentModel, Models: currentModels, IsModelGroup: isModelGroup, DisableModels: disableModels,
                Spec: currentSpec, Specs: currentSpecs, DisableSpecs: disableSpecs,
                ClassificationCategory: currentClassificationCategory, ClassificationCategories: currentClassificationCategories, DisableClassificationCategory: disableClassificationCategory,
                FitsToCategory: currentFitsToCategory, FitsToCategories: currentFitsToCategories,
                DisableEquipmentAttachment: disableEquipmentAttachment,
                Industry: currentIndustry,
                DisableYear: disableYear,
                ListingType: listingType,
                SearchType: SearchType
            };
        });

        if ((listingTypeHasChanged && listingType) || (industryHasChanged && Industry)) {
            if (activeSearch === searchType.attachment) {
                populateSelectList({ drillDownType: drillDownTypes.Category, listingType: listingType });
                populateSelectList({ drillDownType: drillDownTypes.FitsToCategory, forcePopulate: true, listingType: listingType });
            } else if (Industry === classificationConstants.livestock || Industry === classificationConstants.haystraw) {
                if (Industry === classificationConstants.haystraw) {
                    populateSelectList({ drillDownType: drillDownTypes.Category, listingType: listingType, industry: Industry, forcePopulate: true });
                    populateSelectList({ drillDownType: drillDownTypes.Spec, category: category, listingType: listingType, industry: Industry, forcePopulate: true });
                    populateSelectList({ drillDownType: drillDownTypes.ClassificationCategory, category: category, industry: Industry, forcePopulate: true })
                } else {
                    populateSelectList({ drillDownType: drillDownTypes.Category, listingType: listingType, industry: Industry });
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            DisableSpecs: true,
                            DisableClassificationCategory: true
                        };
                    });
                }
            } else {
                populateSelectList({ drillDownType: drillDownTypes.Category, listingType: listingType, industry: Industry });
                populateSelectList({ drillDownType: drillDownTypes.Manufacturer, forcePopulate: true, listingType: listingType, industry: Industry });
            }
        }

        if (categoryHasChanged && category) {
            if (useClassification) {
                if (category.value != '' && !isNaN(category)) {
                    !disableClassificationCategory && populateSelectList({ drillDownType: drillDownTypes.ClassificationCategory, category: category, forcePopulate: true });
                    !disableSpecs && populateSelectList({ drillDownType: drillDownTypes.Spec, category: category, forcePopulate: true });
                } else {
                    setSearchData(previousState => {
                        return {
                            ...previousState,
                            DisableSpecs: true,
                            DisableClassificationCategory: true
                        };
                    });
                }
            } else if (activeSearch === searchType.attachment) {
                populateSelectList({ drillDownType: drillDownTypes.FitsToCategory, category: category, forcePopulate: true });
            } else if (!isNaN(category) && props.ShowDrilldownSearchOnBottom) {
                populateSelectList({ drillDownType: drillDownTypes.Manufacturer, category: category, forcePopulate: true });
            }
        }

        if (manuHasChanged && currentManufacturer) {
            !disableModels && populateSelectList({ drillDownType: drillDownTypes.Model, manufacturer: currentManufacturer, forcePopulate: true, isDismantledDrilldown: isDismantledDrilldown });
            if (!props.ShowDrilldownSearchOnBottom) {
                populateSelectList({ drillDownType: drillDownTypes.Category, manufacturer: currentManufacturer, model: defaultSearchData.Model, forcePopulate: true });
            }
        }

        if ((modelHasChanged && currentModel) && !props.ShowDrilldownSearchOnBottom) {
            populateSelectList({ drillDownType: drillDownTypes.Category, manufacturer: currentManufacturer, model: model, forcePopulate: true, isModelGroup: isModelGroup });
        }

        if (specHasChanged && currentSpec && useClassification) {
            populateSelectList({ drillDownType: drillDownTypes.ClassificationCategory, spec: currentSpec, forcePopulate: true });
        }
    };

    const searchTypeChange = (type, newDrilldownTab) => {
        setCategoryDrilldown(true);
        if (!props.UseClassifications) { setUseClassifications(false); }
        setSearchData(defaultSearchData);
        setActiveSearch(type);
        setSelectedSplitDrilldownCategoryTab(newDrilldownTab);
        if (props.AutoSlideCategoryTabs) {
            props.SetSlideIndex(0);
        }
        setOpenedDropdowns([]);
    };

    const truckTabChange = (type) => {
        if (type === homePageCategoryTab) {
            props.SetSlideIndex(props.TrailerBrandIndex);
        }
    };

    const splitHomePageDrilldownCatTabChange = (type, newDrilldownTab) => {
        searchTypeChange(type, newDrilldownTab);
        truckTabChange(type);
    };

    const getDropdownOptions = (options) => {
        if (options && options.length > 0) {
            return options.map(item => {
                return {
                    key: generateKey(item, options),
                    text: item.Text,
                    value: item.Value,
                    isDefault: item.IsDefaultValue,
                    isParent: item.IsParent,
                    parentValue: item.ParentValue,
                    classificationCategoryID: item.CategoryClassificationID,
                }
            });
        } else {
            return [];
        }
    };
    const getLocationDropdownOptions = (options) => {
        if (options && options.length > 0) {
            return options.map(item => {
                return {
                    key: item.value,
                    text: item.Label,
                    value: item.value,
                    isDefault: false,
                    isParent: false,
                }
            });
        } else {
            return [];
        }
    };

    useEffect(function updateCurrentLocation() {
        if (typeof props.CurrentUserFormattedAddress !== 'undefined' && props.CurrentUserFormattedAddress != searchData.CityStateZip) {
            setSearchData(() => {
                return {
                    ...previousState,
                    CityStateZip: props.CurrentUserFormattedAddress,
                    Category: props.Categories?.[0]?.Text || ''
                };
            });
            currentUser(user => {
                return {
                    ...user,
                    CurrentLocation: {
                        ...user.CurrentLocation,
                        FormattedAddress: props.formattedAddress,
                    }
                };
            });
        }
    }, [props.CurrentUserFormattedAddress]);

    const sortWithEnteredLocation = (e, enteredLocation) => {
        getCoordinatesFromAddress((enteredLocation), routes.AjaxGetCoordinatesFromLocationStringUrl)
            .then(location => {
                setSearchData(previousState => {
                    return {
                        ...previousState,
                        CityStateZip: location.formattedAddress,
                        Category: props.Categories?.[0]?.Text || ''
                    };
                });
                currentUser(user => {
                    return {
                        ...user,
                        Latitude: location.Latitude,
                        Longitude: location.Longitude,
                        CurrentLocation: {
                            ...user.CurrentLocation,
                            FormattedAddress: location.formattedAddress
                        },
                        Category: props.Categories?.[0]?.Text || ''
                    };
                });
            });
    };

    const removeFromAttachmentSearch = (option) => {
        if (props.ExcludeFromAttachmentScopeCategories) {
            if (!props.UseClassifications) { setUseClassifications(false); }
            setCategoryDrilldown(true);
            if (props.Industries.length > 0 && option === searchType.attachment) {
                let industries = props.Industries;
                setSearchData({
                    ...defaultSearchData,
                    SearchType: option,
                    Industries: industries.filter(item => !props.ExcludeFromAttachmentScopeCategories.includes(parseInt(item.UrlNormalizedValue)))
                }
                );
            }
            else if (props.Industries.length > 0 && option === searchType.equipment) {
                setSearchData({
                    ...defaultSearchData,
                    SearchType: option,
                    Industries: props.Industries
                }
                );
            }
        }
        else {
            setSearchData({ ...defaultSearchData, SearchType: option });
        }
    };

    const getCategoryNameFromID = (categoryList, id) => {
        if (categoryList && categoryList.length > 0 && id && !isNaN(id)) {
            return categoryList.filter(item => item.Value == id)[0]?.name || categoryList.filter(item => item.Value == id)[0]?.Text || translator.translate('sandhillssystem.allcategories');
        }
        return id;
    };

    const clearInput = (drilldownType) => {
        if (!openedDropdowns.includes(drilldownType)) {
            setOpenedDropdowns([...openedDropdowns, drilldownType]);
            setPrevValues(prev => {
                return {
                    ...prev,
                    [drilldownType]: searchData[drilldownType]
                }
            });
            setSearchData(previousState => {
                return {
                    ...previousState,
                    [drilldownType]: ''
                }
            });
        }
    };

    const restoreInput = (drilldownType) => {
        if (searchData[drilldownType] == '') {
            setOpenedDropdowns(prev => prev.filter(t => t !== drilldownType));
            setSearchData(previousState => {
                return {
                    ...previousState,
                    [drilldownType]: prevValues[drilldownType]
                }
            })
        }
    }

    const getAutocompleteAdditionalData = () => {
        let additionalData = {};
        if (onlineAuction) {
            additionalData.ListingType = encodeURIComponent('Upcoming Auctions');
            additionalData.EventCategoryID = encodeURIComponent(eventCategory.onlineAuction);
        } else if (onSiteAuction) {
            additionalData.ListingType = encodeURIComponent('Upcoming Auctions');
            additionalData.EventCategoryID = encodeURIComponent(eventCategory.onSiteAuction);
        } else if (props.IsRY) {
            additionalData.ListingType = encodeURIComponent('For Rent');
        }
        return additionalData;
    };

    const getMobileSuffix = () => {
        return windowSize.isMobile ? '-mobile' : '';
    };

    const isOptionEqualToValue = (option, value) => {
        if (!option && !value) return true;

        const rawOptionValue = option?.value ?? null;
        const rawOptionText = option?.text ?? null;

        const rawValueValue = value?.value ?? value ?? null;
        const rawValueText = value?.text ?? null;

        // ⭐ If the selected value is the empty string, only match options whose value is also ""
        if (rawValueValue === "" && rawOptionValue === "") {
            return true;
        }

        const optionComparable = rawOptionValue || rawOptionText;
        const valueComparable = rawValueValue || rawValueText;

        return optionComparable === valueComparable;
    };

    function countIndent(text, size = 2) {
        if (!text) return 0;
        const normalized = text.replace(/&nbsp;/g, "\u00A0");
        let count = 0;
        while (normalized[count] === "\u00A0") count++;
        return Math.floor(count / size);
    }

    function removeIndent(text) {
        if (!text) return text;
        return text.replace(/^(?:\u00A0|&nbsp;)+/, "");
    }

    const getCategoryOptionLabel = (option, categoryList = searchData.Categories) => {
        const text = option?.text || getCategoryNameFromID(categoryList, option);
        return removeIndent(text);
    };

    const renderCategoryOption = (props, option) => {
        const rawText = option.text;
        const indent = countIndent(rawText);
        const cleanText = removeIndent(rawText);

        return (
            <Box
                component="li"
                {...props}
                key={option.key}
                option={option}
                sx={{
                    display: "block",
                    p: 0,
                }}
            >
                <Box
                    sx={{
                        pl: indent * 2,
                        display: "block",
                        whiteSpace: "normal",
                    }}
                >
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: option.isParent ? "bold" : "normal",
                            fontSize: '0.8125rem',
                        }}
                    >
                        {cleanText}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const createCategoryListbox = (categoryList) => {
        return forwardRef(({ children, ...other }, ref) => {
            const items = Children.toArray(children);
            const options = getDropdownOptions(categoryList);
            const groups = groupItemsByParent(items, options);

            return (
                <ul {...other} ref={ref} style={{ padding: 0, border: 'none' }}>
                    {groups.map((group, index) => (
                        <div key={index} style={{ position: 'relative', listStyle: 'none' }}>
                            {group.parent && (
                                <div style={stickyHeaderStyle}>
                                    {group.parent}
                                </div>
                            )}
                            {group.children}
                        </div>
                    ))}
                </ul>
            );
        });
    };


    function groupItemsByParent(items, options) {
        const groups = [];
        let currentGroup = null;
        let currentParentItem = null;

        for (const item of items) {
            const option = item.props.option || null;

            if (!option) {
                // Standalone item
                groups.push({ parent: null, children: [item] });
                currentGroup = null;
                currentParentItem = null;
                continue;
            }

            // Get indentation level from the option's text
            const indent = countIndent(option.text);

            // Determine if this is a sticky parent (no indentation or parentValue is "0")
            const isStickyParent = indent === 0 || option.parentValue === 0;

            if (isStickyParent) {
                // Start a new group with this parent
                currentParentItem = item;
                currentGroup = { parent: item, children: [] };
                groups.push(currentGroup);
            } else if (currentGroup) {
                // Add to the current group
                currentGroup.children.push(item);
            } else {
                groups.push({ parent: null, children: [item] });
            }
        }

        return groups;
    }

    const stickyHeaderStyle = {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: theme.mainMode === 'dark' ? '#121212' : '#fff',
    };

    return (props.ShowSearchForm &&
        (props.DismantledSearch ?
            <div className='dismantled-search-container'>
                <strong className='dismantled-label'>
                    <TranslationText tKey='sandhills.search' />
                    {' ' + props.DisplayText}
                </strong>
                <div className='dismantled-drill-search'>
                    <div className='dismantled-select'>
                        <label htmlFor={'dismantled-search-manufacturer' + getMobileSuffix()} className='visually-hidden'><TranslationText tKey='sandhills.selectmanufacturer' /></label>
                        <Combobox
                            name='Manufacturer'
                            id={'dismantled-search-manufacturer' + getMobileSuffix()}
                            value={searchData.Manufacturer}
                            onChange={(event, option, reason, details) => drilldownChange({ manufacturer: option.value, isDismantledDrilldown: true })}
                            options={getDropdownOptions(searchData.Manufacturers)}
                            getOptionLabel={(option) => option.text || option}
                            onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Manufacturer, isDismantledDrilldown: true })}
                            textFieldStyles={comboboxStyles}
                            styles={{ bgVariant: props.bgVariant }}
                            disableClearable
                            popperStyles={comboboxPopperStyles}
                            ListboxProps={listboxProps}
                            isOptionEqualToValue={isOptionEqualToValue}
                        />
                    </div>
                    <div className='dismantled-select'>
                        <label htmlFor={'dismantled-search-model' + getMobileSuffix()} className='visually-hidden'><TranslationText tKey='sandhills.selectmodel' /></label>
                        <Combobox
                            name='Model'
                            id={'dismantled-search-model' + getMobileSuffix()}
                            value={searchData.Model}
                            onChange={(event, option, reason, details) => drilldownChange({ model: option.value, isModelGroup: option.isParent, isDismantledDrilldown: true })}
                            options={getDropdownOptions(searchData.Models)}
                            getOptionLabel={(option) => option.text || option}
                            onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Model, isDismantledDrilldown: true })}
                            textFieldStyles={comboboxStyles}
                            styles={{ bgVariant: props.bgVariant }}
                            disableClearable
                            disabled={searchData.DisableModels}
                            popperStyles={comboboxPopperStyles}
                            ListboxProps={listboxProps}
                            isOptionEqualToValue={isOptionEqualToValue}
                        />
                    </div>
                    <input
                        className='drill-search-submit ts-button'
                        type='submit'
                        onClick={(e) => drilldownSearch(e, true)}
                        value={translator.translate('sandhills.search')}
                    />
                </div>
            </div>
            :
            <div className='drill-container'>
                <CategoryInformation {...categoryInfo} />
                <div className={getDrillButtonsCSSClass()}>
                    {props.ShowKeywordSearch && props.ShowKeywordSearchFirst &&
                        <h3>
                            <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") searchTypeChange(searchType.keyword) }} className={'ts-button drill-button' + (activeSearch === searchType.keyword ? ' active' : '')} onClick={() => searchTypeChange(searchType.keyword)}>
                                <TranslationText tKey='sandhills.keywords' />
                            </a>
                        </h3>}
                    {props.HomePageDrillDownRentalCompaniesTab &&
                        <h3>
                            <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") searchTypeChange(searchType.findRentalCompanies) }} className={'ts-button drill-button' + (activeSearch === searchType.findRentalCompanies ? ' active' : '')} onClick={() => searchTypeChange(searchType.findRentalCompanies)}>
                                <span>{translator.translate('sandhills.tradesites.mvc.findrentalcompanies')}</span>
                            </a>
                        </h3>
                    }
                    {props.HomePageSplitDrilldownCategoryTabs ?
                        props.HomePageSplitDrilldownCategoryTabs.map(item => {
                            return (
                                <h3 key={item.Key}>
                                    <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") splitHomePageDrilldownCatTabChange(item.Key, item) }} className={'ts-button drill-button' + (activeSearch === item.Key ? ' active' : '')} onClick={() => splitHomePageDrilldownCatTabChange(item.Key, item)}>
                                        <span>{translator.translate(item.LabelTranslationKey)}</span>
                                    </a>
                                </h3>)
                        })
                        :
                        <>
                            {props.EquipmentText &&
                                <h3>
                                    <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") searchTypeChange(searchType.equipment) }} className={'ts-button drill-button' + (activeSearch === searchType.equipment ? ' active' : '')} onClick={() => searchTypeChange(searchType.equipment)}>
                                        <span>{props.EquipmentText}</span>
                                    </a>
                                </h3>
                            }
                        </>
                    }
                    {(props.AttachmentScopeCategories && props.AttachmentScopeCategories.length > 0) && props.ShowAttachments &&
                        <h3>
                            <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") searchTypeChange(searchType.attachment) }} className={'ts-button drill-button' + (activeSearch === searchType.attachment ? ' active' : '')} onClick={() => searchTypeChange(searchType.attachment)}>
                                <span>{props.AttachmentText}</span>
                            </a>
                        </h3>}
                    {props.ShowAuctionResults &&
                        <h3>
                            <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") searchTypeChange(searchType.auctionResult) }} className={'ts-button drill-button' + (activeSearch === searchType.auctionResult ? ' active' : '')} onClick={() => searchTypeChange(searchType.auctionResult)}>
                                <span><TranslationText tKey='sandhills.tradepubwebsite.mvc.auctionresults' /></span>
                            </a>
                        </h3>}
                    {props.ShowKeywordSearch && !props.ShowKeywordSearchFirst &&
                        <h3>
                            <a type='button' role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter") searchTypeChange(searchType.keyword) }} className={'ts-button drill-button' + (activeSearch === searchType.keyword ? ' active' : '')} onClick={() => searchTypeChange(searchType.keyword)}>
                                <TranslationText tKey='sandhills.keywords' />
                            </a>
                        </h3>}
                </div>
                {(activeSearch === searchType.equipment || activeSearch === searchType.auctionResult || props.HomePageSplitDrilldownCategoryTabs?.some(o => o.Key === activeSearch)) &&
                    <div className='drill-content'>
                        <div className='drill-search'>
                            {((activeSearch === searchType.equipment || activeSearch === searchType.auctionResult) && searchData.Industries?.length > 0) &&
                                <>
                                    <label htmlFor='drilldown-search-industry' className='visually-hidden'>Market</label>
                                    <Combobox
                                        name='ListingType'
                                        id='drilldown-search-industry'
                                        value={searchData.Industry}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => {
                                            let val = option?.value || option;
                                            drilldownChange({ Industry: val });
                                        }}
                                        options={getDropdownOptions(searchData.Industries)}
                                        getOptionLabel={(option) => option.text || option}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Industries })}
                                        onFocus={() => clearInput("Industry")}
                                        onBlur={() => restoreInput("Industry")}
                                        disableClearable
                                        getOptionLabel={(option) => option?.text || getCategoryNameFromID(searchData.Industries, option)}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>
                            }
                            {(activeSearch === searchType.equipment && searchData.ListingTypesDropdown?.length > 0) &&
                                <>
                                    <label htmlFor='drilldown-search-listingType' className='visually-hidden'><TranslationText tKey='sandhills.listingtype' /></label>
                                    <Combobox
                                        name='ListingType'
                                        id='drilldown-search-listingType'
                                        value={searchData.ListingType}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => drilldownChange({ listingType: option.value })}
                                        options={getDropdownOptions(searchData.ListingTypesDropdown)}
                                        getOptionLabel={(option) => option.text || option}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.ListingType })}
                                        onFocus={() => clearInput(qsParams.ListingType)}
                                        onBlur={() => restoreInput(qsParams.ListingType)}
                                        disableClearable
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>
                            }
                            {(activeSearch === searchType.auctionResult && props.AttachmentScopeCategories && props.AttachmentScopeCategories.length > 0 && !(currentSite?.Site?.IsAuctionTime || currentSite?.Site?.IsBidcaller)) &&
                                <>
                                    <label htmlFor='drilldown-search-type' className='visually-hidden'><TranslationText tKey='sandhills.selectasearchtype' /></label>
                                    <Combobox
                                        name='Type'
                                        id='drilldown-search-type'
                                        value={searchTypeOptions.find(type => type.value === searchData.SearchType)}
                                        onChange={(event, option, reason, details) => removeFromAttachmentSearch(option.value)}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        options={searchTypeOptions}
                                        getOptionLabel={(option) => option.text || option}
                                        disableClearable
                                        onFocus={() => clearInput("SearchType")}
                                        onBlur={() => restoreInput("SearchType")}
                                        disabled={searchData.DisableEquipmentAttachment}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>}
                            <label htmlFor='drilldown-search-category' className='visually-hidden'><TranslationText tKey='sandhills.tradesites.mvc.selectacategory' /></label>
                            {categoryDrilldown && props.ShowDrilldownSearchOnBottom &&
                                <>
                                    <Combobox
                                        name='Category'
                                        id='drilldown-search-category'
                                        value={searchData.Category}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        disableClearable
                                        onChange={(event, option, reason, details) => {
                                            let val = option?.value !== '' ? option?.value : option;
                                            drilldownChange({ category: val });
                                            if (val) {
                                                setCategoryInfo({ CategoryID: val, CategoryName: getCategoryNameFromID(searchData.Categories, val) });
                                            }
                                        }}
                                        options={getDropdownOptions(searchData.Categories)}
                                        getOptionLabel={getCategoryOptionLabel}
                                        renderOption={renderCategoryOption}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Category })}
                                        onFocus={() => clearInput(qsParams.Category)}
                                        onBlur={() => restoreInput(qsParams.Category)}
                                        disabled={searchData.DisableCategories}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxComponent={createCategoryListbox(searchData.Categories)}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>
                            }
                            {useClassification ?
                                <>
                                    <label htmlFor='drilldown-search-breedtype' className='visually-hidden'><TranslationText tKey='sandhills.breedcrop' /></label>
                                    <Combobox
                                        name='Spec'
                                        id='drilldown-search-breedtype'
                                        value={searchData.Spec}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => drilldownChange({ spec: option?.value || option })}
                                        options={getDropdownOptions(searchData.Specs)}
                                        getOptionLabel={(option) => option.text || option}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Spec })}
                                        onFocus={() => clearInput("Spec")}
                                        onBlur={() => restoreInput("Spec")}
                                        disableClearable
                                        disabled={searchData.DisableSpecs}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                    <label htmlFor='drilldown-search-classification-category' className='visually-hidden'><TranslationText tKey='sandhills.drilldownsearch.webapp.selectclasscategory' /></label>
                                    <Combobox
                                        name='ClassificationCategory'
                                        id='drilldown-search-classification-category'
                                        value={searchData.ClassificationCategory}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => drilldownChange({ classificationCategory: option.value })}
                                        options={getDropdownOptions(searchData.ClassificationCategories)}
                                        getOptionLabel={(option) => option?.text || isNaN(option) && option || getCategoryNameFromID(searchData.ClassificationCategories, option)}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.ClassificationCategory })}
                                        onFocus={() => clearInput(qsParams.ClassificationCategory)}
                                        onBlur={() => restoreInput(qsParams.ClassificationCategory)}
                                        disableClearable
                                        disabled={searchData.DisableClassificationCategory}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </> : <>
                                    <label htmlFor='drilldown-search-manufacturer' className='visually-hidden'><TranslationText tKey='sandhills.selectmanufacturer' /></label>
                                    <Combobox
                                        name='Manufacturer'
                                        id='drilldown-search-manufacturer'
                                        value={searchData.Manufacturer}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => drilldownChange({ manufacturer: option.value })}
                                        options={getDropdownOptions(searchData.Manufacturers)}
                                        getOptionLabel={(option) => option.text || option}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Manufacturer })}
                                        onFocus={() => clearInput(qsParams.Manufacturer)}
                                        onBlur={() => restoreInput(qsParams.Manufacturer)}
                                        disableClearable
                                        disabled={searchData.DisableManufacturers}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                    <label htmlFor='drilldown-search-model' className='visually-hidden'><TranslationText tKey='sandhills.selectmodel' /></label>
                                    <Combobox
                                        name='Model'
                                        id='drilldown-search-model'
                                        value={searchData.Model}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => {
                                            let val = option?.value;
                                            let isModelGroup = option?.isParent;
                                            drilldownChange({ model: val, isModelGroup: isModelGroup });
                                        }}
                                        options={getDropdownOptions(searchData.Models)}
                                        getOptionLabel={(option) => option.text || option}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Model })}
                                        onFocus={() => clearInput(qsParams.Model)}
                                        onBlur={() => restoreInput(qsParams.Model)}
                                        disableClearable
                                        disabled={searchData.DisableModels}
                                        popperStyles={comboboxPopperStyles}
                                        showEndAdornment={props.ShowDrilldownSearchOnBottom}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>}
                            {categoryDrilldown && !props.ShowDrilldownSearchOnBottom &&
                                <>
                                    <Combobox
                                        name='Category'
                                        id='drilldown-search-category'
                                        value={getCategoryNameFromID(searchData.Categories, searchData.Category)}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        disableClearable
                                        onChange={(event, option, reason, details) => {
                                            let val = option?.value || option;
                                            let classificationCategory = option?.classificationCategoryID;
                                            drilldownChange({ category: val });
                                            if (val) {
                                                setCategoryInfo({ CategoryID: val, CategoryName: getCategoryNameFromID(searchData.Categories, val), ClassificationCategoryID: classificationCategory });
                                            }
                                        }}
                                        options={getDropdownOptions(searchData.Categories)}
                                        getOptionLabel={getCategoryOptionLabel}
                                        renderOption={renderCategoryOption}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Category })}
                                        onFocus={() => clearInput(qsParams.Category)}
                                        onBlur={() => restoreInput(qsParams.Category)}
                                        disabled={searchData.DisableCategories}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxComponent={createCategoryListbox(searchData.Categories)}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                    <PopoverMenu
                                        IsYear
                                        Label='Year Range'
                                        YearRange={searchData.YearRange}
                                        disabled={searchData.DisableYear}
                                        onChange={(option) => {
                                            let val = option?.text || option;
                                            setSearchData(previousState => {
                                                return {
                                                    ...previousState,
                                                    YearRange: val
                                                };
                                            });
                                        }}
                                    />
                                    <PopoverMenu
                                        DateRange={searchData.DateRange}
                                        Label='Date Range'
                                        onChange={(option) => {
                                            let val = option?.text || option;
                                            setSearchData(previousState => {
                                                return {
                                                    ...previousState,
                                                    DateRange: val
                                                };
                                            });
                                        }}
                                    />
                                </>
                            }
                            <input
                                className='drill-search-submit ts-button'
                                type='submit'
                                onClick={drilldownSearch}
                                value={translator.translate('sandhills.search')}
                            />
                        </div>
                    </div>}
                {activeSearch === searchType.attachment &&
                    <div className='drill-content'>
                        <div className='drill-search'>
                            {searchData.ListingTypesDropdown?.length > 0 &&
                                <>
                                    <label htmlFor='drilldown-search-listingType' className='visually-hidden'><TranslationText tKey='sandhills.listingtype' /></label>
                                    <Combobox
                                        name='ListingType'
                                        id='drilldown-search-listingType'
                                        value={searchData.ListingType}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        onChange={(event, option, reason, details) => drilldownChange({ listingType: option.value })}
                                        options={getDropdownOptions(searchData.ListingTypesDropdown)}
                                        getOptionLabel={(option) => option.text || option}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.ListingType })}
                                        onFocus={() => clearInput(qsParams.ListingType)}
                                        onBlur={() => restoreInput(qsParams.ListingType)}
                                        disableClearable
                                        popperStyles={comboboxPopperStyles}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>
                            }
                            <label htmlFor='drilldown-search-attachment-category' className='visually-hidden'><TranslationText tKey='sandhills.selectanattachmentcategory' /></label>
                            <Combobox
                                name='AttachmentCategory'
                                id='drilldown-search-attachment-category'
                                value={searchData.Category}
                                textFieldStyles={comboboxStyles}
                                styles={{ bgVariant: props.bgVariant }}
                                disableClearable
                                onChange={(event, option, reason, details) => {
                                    let val = option?.value || option;
                                    drilldownChange({ category: val });
                                    if (val) {
                                        setCategoryInfo({ CategoryID: val, CategoryName: getCategoryNameFromID(searchData.Categories, val) });
                                    }
                                }}
                                options={getDropdownOptions(searchData.Categories)}
                                getOptionLabel={getCategoryOptionLabel}
                                renderOption={renderCategoryOption}
                                onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Category })}
                                onFocus={() => clearInput(qsParams.Category)}
                                onBlur={() => restoreInput(qsParams.Category)}
                                popperStyles={comboboxPopperStyles}
                                ListboxComponent={createCategoryListbox(searchData.Categories)}
                                ListboxProps={listboxProps}
                                isOptionEqualToValue={isOptionEqualToValue}
                            />
                            {props.ShowFitsToDropdown &&
                                <>
                                    <label htmlFor='drilldown-search-fits-to-category' className='visually-hidden'><TranslationText tKey='sandhills.selectafitstocategory' /></label>
                                    <Combobox
                                        name='FitsToCategory'
                                        id='drilldown-search-fits-to-category'
                                        value={searchData.FitsToCategory}
                                        textFieldStyles={comboboxStyles}
                                        styles={{ bgVariant: props.bgVariant }}
                                        disableClearable
                                        onChange={(event, option, reason, details) => {
                                            let val = option?.value || option;
                                            drilldownChange({ fitsToCategory: val });
                                            if (val && !isNaN(val)) {
                                                setCategoryInfo({ CategoryID: val, CategoryName: getCategoryNameFromID(searchData.FitsToCategories, val) });
                                            }
                                        }}
                                        options={getDropdownOptions(searchData.FitsToCategories)}
                                        getOptionLabel={(option) => getCategoryOptionLabel(option, searchData.FitsToCategories)}
                                        renderOption={renderCategoryOption}
                                        onOpen={() => populateSelectList({ drillDownType: drillDownTypes.FitsToCategory })}
                                        onFocus={() => clearInput("FitsToCategory")}
                                        onBlur={() => restoreInput("FitsToCategory")}
                                        popperStyles={comboboxPopperStyles}
                                        ListboxComponent={createCategoryListbox(searchData.FitsToCategories)}
                                        ListboxProps={listboxProps}
                                        isOptionEqualToValue={isOptionEqualToValue}
                                    />
                                </>
                            }
                            <input
                                className='drill-search-submit ts-button'
                                type='submit'
                                onClick={drilldownSearch}
                                value={translator.translate('sandhills.search')}
                            />
                        </div>
                    </div>}
                {activeSearch === searchType.keyword &&
                    <div className='drill-content'>
                        <div className='drill-search'>
                            <Autocomplete
                                label={props.PlaceHolderText}
                                ajaxOptions={{ additionalData: getAutocompleteAdditionalData() }}
                                searchOptions={{ additionalData: getAutocompleteAdditionalData() }}
                                styles={{ bgVariant: props.bgVariant }}
                                autocompleteSearchField={true}
                                disableClearable={false}
                                SearchURLs={props.SearchURLs}
                            />
                        </div>
                    </div>}

                {activeSearch === searchType.findRentalCompanies &&
                    <div className='drill-content'>
                        <div className='drill-search'>
                            <label htmlFor='drilldown-search-citystatezip' className='visually-hidden'><TranslationText tKey='sandhills.citystatezip' /></label>
                            <Autocomplete
                                name='CityStateZip'
                                id='drilldown-search-citystatezip'
                                hideLabel={true}
                                value={searchData.CityStateZip}
                                textFieldStyles={windowSize.isMobile ? cityStateZipMobileComboboxStyles : cityStateZipStylesDesktop}
                                styles={{ bgVariant: props.bgVariant }}
                                onChange={(event, option, reason, details) => {
                                    let val = option?.value || option?.Text || option || "";
                                    setSearchData({ CityStateZip: val, Categories: props.Categories });
                                    if (val != "" && val != null) {
                                        sortWithEnteredLocation(event, val);
                                    }
                                }}
                                options={getLocationDropdownOptions(searchData.Locations)}
                                popperStyles={comboboxPopperStyles}
                                placeholder={cityStateZipPlaceholder}
                                IsCityStateZip={true}
                                ajaxOptions={{ enabled: true, url: routes.LocationAutosuggestUrl }}
                                disableClearable={false}
                                sx={{
                                    // Target the placeholder text
                                    "& .MuiInputBase-input::placeholder": {
                                        color: "black",
                                        opacity: 1, // Ensure full opacity
                                    },
                                }}
                            />
                            <label htmlFor='drilldown-search-dealer-category' className='visually-hidden'><TranslationText tKey='sandhills.tradesites.mvc.selectacategory' /></label>
                            <Combobox
                                name='Category'
                                id='drilldown-search-dealer-category'
                                value={searchData.Category}
                                textFieldStyles={comboboxStyles}
                                styles={{ bgVariant: props.bgVariant }}
                                disableClearable
                                onChange={(event, option, reason, details) => {
                                    let val = option?.value || option;
                                    drilldownChange({ category: val });
                                    if (val) {
                                        setCategoryInfo({ CategoryID: val, CategoryName: getCategoryNameFromID(searchData.Categories, val) });
                                    }
                                }}
                                options={getDropdownOptions(searchData.Categories)}
                                getOptionLabel={getCategoryOptionLabel}
                                renderOption={renderCategoryOption}
                                onOpen={() => populateSelectList({ drillDownType: drillDownTypes.Category })}
                                onFocus={() => clearInput(qsParams.Category)}
                                onBlur={() => restoreInput(qsParams.Category)}
                                popperStyles={comboboxPopperStyles}
                                ListboxComponent={createCategoryListbox(searchData.Categories)}
                                ListboxProps={listboxProps}
                                isOptionEqualToValue={isOptionEqualToValue}
                            />
                            <input
                                className='drill-search-submit ts-button'
                                type='submit'
                                onClick={drilldownSearch}
                                value={translator.translate('sandhills.search')}
                            />
                        </div>
                    </div>}
            </div>
        )
    );
};

export default DrillDownSearch;